import React, { useEffect, useState } from 'react';
import DataTable from '../components/DataTable';
import EditModal from '../components/EditModal';
import type { Entry, ApiResponse } from '../types/data';
import Toast from '../components/Toast';

const API_URL = 'https://api.epsonrewards.my/cms/index.php';
const API_TOKEN = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhcGlfa2V5IjoiZXBzb25fYXBpX2FjY2VzcyJ9.8KZZcKpJmqt_hH2_tGFJP9HM8Vp-YSUqWF_kYYpVNx4';

export default function Dashboard() {
  const [allData, setAllData] = useState<Entry[]>([]);
  const [toast, setToast] = useState({ show: false, message: '' });
  const [toastId, setToastId] = useState(0);
  const [filteredData, setFilteredData] = useState<Entry[]>([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_records: '0',
    records_per_page: 10
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentSearch, setCurrentSearch] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [userData, setUserData] = useState<Entry | null>(null);
  const [sortBy, setSortBy] = useState<'created_date_time_asc' | 'created_date_time_desc' | 'model'>('created_date_time_desc');

  const getSortedData = (data: Entry[]) => {
    let sorted = [...data];
    if (sortBy === 'created_date_time_asc') {
      sorted.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    } else if (sortBy === 'created_date_time_desc') {
      sorted.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    } else if (sortBy === 'model') {
      sorted.sort((a, b) => a.model.localeCompare(b.model));
    }
    return sorted;
  };

  const fetchAllData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const url = new URL(API_URL);
      url.searchParams.append('token', API_TOKEN);
      url.searchParams.append('view_all', 'true');

      console.log('Fetching data from:', url.toString()); // Debug log

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.status} ${response.statusText}`);
      }

      const result: ApiResponse = await response.json();
      console.log('API Response:', result); // Debug log

      setAllData(result.data);

      // Reapply the current search term to maintain search state
      if (currentSearch) {
        handleSearch(currentSearch, result.data);
      } else {
        handleSearch('', result.data);
      }
    } catch (err) {
      console.error('Error fetching data:', err); // Debug log
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const handleSearch = (searchTerm: string, dataToSearch = allData) => {
    setCurrentSearch(searchTerm);

    let filtered = dataToSearch;
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = dataToSearch.filter(item => {
        return Object.values(item).some(value =>
          String(value).toLowerCase().includes(search)
        );
      });
    }

    const sortedFiltered = getSortedData(filtered);
    setFilteredData(sortedFiltered);
    const totalRecords = sortedFiltered.length;
    const recordsPerPage = pagination.records_per_page;
    setPagination(prev => ({
      ...prev,
      current_page: 1,
      total_records: totalRecords.toString(),
      total_pages: Math.ceil(totalRecords / recordsPerPage)
    }));
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, current_page: page }));
  };

  const handleRecordsPerPageChange = (recordsPerPage: number) => {
    setPagination(prev => ({
      ...prev,
      records_per_page: recordsPerPage,
      current_page: 1,
      total_pages: Math.ceil(filteredData.length / recordsPerPage)
    }));
  };

  const handleSortChange = (newSortBy: 'created_date_time_asc' | 'created_date_time_desc' | 'model') => {
    setSortBy(newSortBy);
    const sortedFiltered = getSortedData(filteredData);
    setFilteredData(sortedFiltered);
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 transition-colors duration-200">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">Epson Rewards Dashboard</h1>

        {error && (
          <div className="mb-4 p-4 bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 rounded-md">
            <p>{error}</p>
          </div>
        )}

        <DataTable
          data={filteredData.slice(
            (pagination.current_page - 1) * pagination.records_per_page,
            pagination.current_page * pagination.records_per_page
          )}
          allFilteredData={filteredData}
          pagination={pagination}
          onPageChange={handlePageChange}
          onRecordsPerPageChange={handleRecordsPerPageChange}
          onSearch={handleSearch}
          onSortChange={handleSortChange}
          isLoading={isLoading}
          setIsEditModalOpen={setIsEditModalOpen}
          setUserData={setUserData}
        />
      </div>

      {isEditModalOpen && userData && (
        <EditModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          userData={userData}
          toast={toast}
          setToast={setToast}
          toastId={toastId}
          setToastId={setToastId}
          fetchAllData={fetchAllData}
        />
      )}

      {toast.show && (
        <Toast
          message={toast.message}
          onClose={() => setToast({ show: false, message: '' })}
          id={toastId}
        />
      )}
    </div>
  );
}